import { formatInstantDate } from '../../lib/format';
import type { AssistantCredits } from '../../types/api';

/** What spends credits, in the owner's words. Shown beside every meter. */
export const CREDITS_EXPLAINER =
  'Chat is free; every 10th message uses 0.5. Replies that use tools use 0.5, web searches 1, reading a page 0.5.';

/** Under this share left, the meter turns amber. */
const LOW_SHARE = 0.1;

/** A credit figure: whole numbers plain, fractions to one decimal ("142", "1.5"). */
export function formatCredits(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** "Used 1 credit" / "Used 1.5 credits". */
export function creditsUsedLabel(value: number): string {
  return `Used ${formatCredits(value)} ${value === 1 ? 'credit' : 'credits'}`;
}

/** Credits left, never below zero: the last tool turn may overdraw a little. */
export function creditsLeft(credits: AssistantCredits): number {
  return Math.max(0, credits.left);
}

export function isCreditsLow(credits: AssistantCredits): boolean {
  return credits.limit > 0 && creditsLeft(credits) < credits.limit * LOW_SHARE;
}

/** The meter's text: "142 of 200 credits". */
export function creditsMeterText(credits: AssistantCredits): string {
  return `${formatCredits(creditsLeft(credits))} of ${formatCredits(credits.limit)} credits`;
}

/** The 429's sentence, rebuilt when the server's own is not to hand. */
export function creditsExhaustedMessage(credits: AssistantCredits | null): string {
  return credits
    ? `You've used this month's assistant credits. They reset on ${formatInstantDate(credits.resets_at)}.`
    : "You've used this month's assistant credits. They reset at the start of next month.";
}

/**
 * The last credits read per brand. Credits belong to the account, not a
 * thread, so a new chat (no thread read yet) still shows the meter.
 */
const lastCredits = new Map<string, AssistantCredits>();

export function rememberCredits(businessId: string, credits: AssistantCredits | null): void {
  if (credits) lastCredits.set(businessId, credits);
}

export function recalledCredits(businessId: string | null): AssistantCredits | null {
  return businessId ? (lastCredits.get(businessId) ?? null) : null;
}
